/**
 * Polling Coordinator - manages all polling operations
 */

import type { Config } from '../modules/config';
import {
  schedulePolling,
  cancelPolling,
  scheduleCreditPolling,
  cancelCreditPolling,
  scheduleBidRefresh,
  cancelBidRefreshPolling,
  scheduleLogoRefresh,
  cancelLogoRefreshPolling,
  scheduleCountdownUpdates,
} from '../modules/polling';
import { formatCountdown } from '../modules/formatting';

interface PollingCoordinatorDependencies {
  cfg: Config | null;
  mapRemoteDocument: (doc: any) => any;
  render: () => void;
}

/**
 * Start all remote polling operations
 */
export function startAllPolling(deps: PollingCoordinatorDependencies): void {
  const { cfg, mapRemoteDocument, render } = deps;

  if (!cfg) return;

  schedulePolling(cfg, mapRemoteDocument, render);
  scheduleCreditPolling();
  scheduleBidRefresh(cfg, mapRemoteDocument, render);
  scheduleLogoRefresh();
}

/**
 * Stop all remote polling operations
 */
export function stopAllPolling(): void {
  cancelPolling();
  cancelCreditPolling();
  cancelBidRefreshPolling();
  cancelLogoRefreshPolling();
}

/**
 * Initialize countdown updates (always active for displaying close times)
 */
export function initializeCountdownUpdates(): void {
  scheduleCountdownUpdates((closeTs: number) => formatCountdown(closeTs), 10000);
}

/**
 * Handle visibility change - pause/resume polling when tab is hidden/visible
 */
export function setupVisibilityChangeHandling(
  deps: PollingCoordinatorDependencies,
  state: any,
  startRemoteLotsFetch: () => void
): void {
  document.addEventListener('visibilitychange', function () {
    const hidden =
      typeof document.visibilityState === 'string' ? document.visibilityState === 'hidden' : false;

    if (hidden) {
      stopAllPolling();
    } else {
      // Resume polling when tab becomes visible
      // Only trigger a full fetch if we have no lots yet, otherwise just resume polling
      const hasLots = Array.isArray(state.lots) && state.lots.length > 0;
      if (state.mode === 'auth0' && !hasLots) {
        startRemoteLotsFetch();
      }
      startAllPolling(deps);
    }
  });
}
